import { GA4_SCHEMAS } from "../data/ga4Schemas.js";

const RESERVED_TRIGGER_IDS = new Set(["2147479552", "2147479553", "2147479554"]);
const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };
const SEPARATORS = [" - ", " — ", " | ", ": ", "_", "-", " "];
const PLACEHOLDER_NAME = /^(new|test|testing|untitled|copy|tag|trigger|variable)(\s+\d+)?$/i;

export function parseContainerText(text) {
  if (!text || !text.trim()) throw new Error("Paste a GTM export or choose a JSON file first.");

  let raw;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON: ${friendlyJsonError(error)}`);
  }

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("Expected a GTM export object at the root of the JSON.");
  }
  if (!raw.containerVersion || typeof raw.containerVersion !== "object") {
    throw new Error("Missing containerVersion. Export the container from Google Tag Manager and try again.");
  }

  const missing = ["tag", "trigger", "variable"].filter(
    (key) => !Array.isArray(raw.containerVersion[key]),
  );
  if (missing.length) {
    throw new Error(
      `Unexpected GTM export shape. containerVersion.${missing.join(
        ", containerVersion.",
      )} must be an array.`,
    );
  }

  return {
    tags: raw.containerVersion.tag,
    triggers: raw.containerVersion.trigger,
    variables: raw.containerVersion.variable,
    raw,
    metadata: {
      name: raw.containerVersion.container?.name || "Unnamed container",
      publicId: raw.containerVersion.container?.publicId || "",
      exportTime: raw.exportTime || "",
      formatVersion: raw.exportFormatVersion || "",
    },
  };
}

export function parseDataLayerText(text) {
  if (!text || !text.trim()) throw new Error("Paste a dataLayer event first.");
  let raw;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON: ${friendlyJsonError(error)}`);
  }

  const values = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.dataLayer)
      ? raw.dataLayer
      : [raw];

  const events = values
    .filter(
      (entry) =>
        entry &&
        typeof entry === "object" &&
        !Array.isArray(entry) &&
        typeof entry.event === "string",
    )
    .map((entry, index) => {
      const params = { ...entry };
      delete params.event;
      if (
        params.ecommerce &&
        typeof params.ecommerce === "object" &&
        !Array.isArray(params.ecommerce)
      ) {
        Object.assign(params, params.ecommerce);
        delete params.ecommerce;
      }
      return {
        eventName: entry.event,
        params,
        item: `${entry.event} · push ${index + 1}`,
        paused: false,
        source: "dataLayer",
      };
    });

  if (!events.length) throw new Error("No objects with an event property were found.");
  return events;
}

export function analyseContainer(container, options = {}) {
  const inferredRules = inferNamingConventions(container);
  const namingRules = options.namingRules || inferredRules;
  const healthFindings = auditHealth(container);
  const namingFindings = auditNaming(container, namingRules, options.includePaused ?? true);
  const autoEvents = extractGa4Events(container.tags);
  const manualEvents = options.manualEvents || [];
  const eventFindings = validateEvents([...autoEvents, ...manualEvents]);
  const findings = addFindingIds(
    sortFindings([...healthFindings, ...namingFindings, ...eventFindings]),
  );
  const inventory = makeInventory(container);
  const ratings = calculateRatings({
    container,
    findings,
    eventCount: autoEvents.length + manualEvents.length,
  });

  return {
    inferredRules,
    namingRules,
    autoEvents,
    manualEvents,
    inventory,
    findings,
    ratings,
    counts: {
      tags: container.tags.length,
      triggers: container.triggers.length,
      variables: container.variables.length,
      activeTags: container.tags.filter((tag) => tag.paused !== true).length,
      pausedTags: container.tags.filter((tag) => tag.paused === true).length,
      ga4Events: autoEvents.length,
    },
  };
}

export function inferNamingConventions(container) {
  return {
    tag: inferRule(container.tags.map((item) => item.name).filter(Boolean)),
    trigger: inferRule(container.triggers.map((item) => item.name).filter(Boolean)),
    variable: inferRule(container.variables.map((item) => item.name).filter(Boolean)),
  };
}

function inferRule(names) {
  if (!names.length) {
    return {
      prefixes: [],
      separator: " - ",
      casing: "title",
      confidence: 0,
      sampleSize: 0,
      parsedRatio: 0,
      separatorConsensus: 0,
      casingConsensus: 0,
      source: "auto",
    };
  }

  const parsed = names.map(parseStructuredName).filter(Boolean);
  const separatorCounts = countBy(parsed.map((item) => item.separator));
  const prefixCounts = countBy(parsed.map((item) => item.prefix));
  const casingCounts = countBy(parsed.map((item) => detectCasing(item.remainder)));
  const separator = topEntry(separatorCounts)?.[0] || " - ";
  const casing = topEntry(casingCounts)?.[0] || "title";
  const parsedRatio = parsed.length / names.length;
  const separatorConsensus = parsed.length
    ? (separatorCounts.get(separator) || 0) / parsed.length
    : 0;
  const casingConsensus = parsed.length
    ? (casingCounts.get(casing) || 0) / parsed.length
    : 0;
  const threshold = Math.max(2, Math.ceil(parsed.length * 0.08));
  let prefixes = [...prefixCounts.entries()]
    .filter(([, count]) => count >= threshold)
    .sort((left, right) => right[1] - left[1])
    .slice(0, 8)
    .map(([prefix]) => prefix);

  if (!prefixes.length && parsed.length) {
    prefixes = [...prefixCounts.entries()]
      .sort((left, right) => right[1] - left[1])
      .slice(0, Math.min(3, prefixCounts.size))
      .map(([prefix]) => prefix);
  }

  const sampleAdjustment = Math.min(1, names.length / 8);
  const confidence = Math.round(
    ((parsedRatio + separatorConsensus + casingConsensus) / 3) * 100 * sampleAdjustment,
  );

  return {
    prefixes,
    separator,
    casing,
    confidence,
    sampleSize: names.length,
    parsedRatio: Math.round(parsedRatio * 100),
    separatorConsensus: Math.round(separatorConsensus * 100),
    casingConsensus: Math.round(casingConsensus * 100),
    source: "auto",
  };
}

function parseStructuredName(name) {
  const value = String(name).trim();
  let selected = null;
  for (const separator of SEPARATORS) {
    const index = value.indexOf(separator);
    if (index <= 0) continue;
    if (!selected || index < selected.index || (index === selected.index && separator.length > selected.separator.length)) {
      selected = { index, separator };
    }
  }
  if (!selected) return null;
  const prefix = value.slice(0, selected.index).trim();
  const remainder = value.slice(selected.index + selected.separator.length).trim();
  if (!prefix || !remainder || prefix.length > 24) return null;
  return { prefix, remainder, separator: selected.separator };
}

function detectCasing(value) {
  if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(value)) return "snake";
  if (/^[a-z0-9]+(?:-[a-z0-9]+)+$/.test(value)) return "kebab";
  if (/^[a-z0-9]+(?:\s+[a-z0-9]+)*$/.test(value)) return "lower";
  if (/^[A-Z0-9]+(?:[_\s-][A-Z0-9]+)*$/.test(value)) return "upper";
  const words = value.split(/\s+/).filter(Boolean);
  if (
    words.length &&
    words.every(
      (word) =>
        /^[A-Z0-9][A-Za-z0-9'()/.]*$/.test(word) ||
        /^[A-Z0-9]{2,}$/.test(word),
    )
  ) {
    return "title";
  }
  return "mixed";
}

export function auditHealth(container) {
  const findings = [];
  const triggerIds = new Set(container.triggers.map((trigger) => String(trigger.triggerId)));
  const referencedTriggerIds = new Set();

  container.tags.forEach((tag) => {
    [...asArray(tag.firingTriggerId), ...asArray(tag.blockingTriggerId)].forEach((id) =>
      referencedTriggerIds.add(String(id)),
    );
  });

  container.triggers.forEach((trigger) => {
    const id = String(trigger.triggerId);
    if (!referencedTriggerIds.has(id) && !isReservedTrigger(trigger)) {
      findings.push(
        finding(
          "health",
          "Orphaned trigger",
          "warning",
          trigger.name || `Trigger ${id}`,
          "No tag references this trigger as a firing or blocking condition.",
        ),
      );
    }
  });

  const variableReferences = new Set();
  [container.tags, container.triggers, container.variables].forEach((collection) =>
    collection.forEach((item) => collectVariableReferences(item, variableReferences)),
  );
  container.variables.forEach((variable) => {
    if (variable.name && !variableReferences.has(variable.name)) {
      findings.push(
        finding(
          "health",
          "Orphaned variable",
          "warning",
          variable.name,
          "This custom variable is never referenced by a tag, trigger, or another variable.",
        ),
      );
    }
  });

  container.tags.forEach((tag) => {
    const name = tag.name || `Tag ${tag.tagId || "unknown"}`;
    const firingIds = asArray(tag.firingTriggerId).map(String);
    const blockingIds = asArray(tag.blockingTriggerId).map(String);

    if (!tag.name) {
      findings.push(finding("health", "Unnamed tag", "error", name, "The tag has no readable name."));
    }
    if (tag.paused === true) {
      findings.push(
        finding("health", "Paused tag", "warning", name, "The tag is paused and cannot fire."),
      );
    }
    if (!firingIds.length) {
      findings.push(
        finding("health", "No trigger set", "error", name, "The tag has no firing trigger."),
      );
    }
    [...firingIds, ...blockingIds].forEach((id) => {
      if (!triggerIds.has(id) && !RESERVED_TRIGGER_IDS.has(id)) {
        findings.push(
          finding(
            "health",
            "Dangling trigger reference",
            "error",
            name,
            `References trigger ID ${id}, which is absent from this export.`,
          ),
        );
      }
    });
    if (!hasExplicitConsent(tag)) {
      findings.push(
        finding(
          "health",
          "Consent not explicit",
          "warning",
          name,
          "No meaningful consent decision is configured for this tag.",
        ),
      );
    }
  });

  const duplicateGroups = new Map();
  container.triggers
    .filter((trigger) => !isReservedTrigger(trigger))
    .forEach((trigger) => {
      const filters = [
        ...asArray(trigger.filter),
        ...asArray(trigger.autoEventFilter),
        ...asArray(trigger.customEventFilter),
      ];
      const filterSignatures = filters
        .map((filter) => stableStringify(stripIdentityFields(filter)))
        .sort();
      const signature = `${trigger.type || "UNKNOWN"}::${filterSignatures.join("|")}`;
      if (!duplicateGroups.has(signature)) duplicateGroups.set(signature, []);
      duplicateGroups.get(signature).push(trigger);
    });

  duplicateGroups.forEach((group) => {
    if (group.length < 2) return;
    findings.push(
      finding(
        "health",
        "Redundant trigger cluster",
        "warning",
        group.map((trigger) => trigger.name || `Trigger ${trigger.triggerId}`).join(" ↔ "),
        "These triggers share a type and structurally identical filter conditions.",
      ),
    );
  });

  Object.entries({
    tag: container.tags,
    trigger: container.triggers,
    variable: container.variables,
  }).forEach(([type, items]) => {
    const groups = groupBy(items, (item) => normalizeName(item.name || ""));
    groups.forEach((group, normalized) => {
      if (!normalized || group.length < 2) return;
      findings.push(
        finding(
          "health",
          "Exact duplicate name",
          "warning",
          group.map((item) => item.name).join(" ↔ "),
          `Multiple ${type}s share the same normalized name.`,
        ),
      );
    });
  });

  return sortFindings(findings);
}

export function auditNaming(container, rules, includePaused = true) {
  const findings = [];
  const collections = {
    tag: container.tags.filter((tag) => includePaused || tag.paused !== true),
    trigger: container.triggers,
    variable: container.variables,
  };

  Object.entries(collections).forEach(([type, items]) => {
    const rule = rules[type];
    if (!rule) return;
    if (rule.confidence < 40 && rule.source === "auto") {
      findings.push(
        finding(
          "naming",
          "Low inference confidence",
          "info",
          capitalize(type),
          `Only ${rule.confidence}% confidence was available from ${rule.sampleSize} names; review the inferred rule.`,
        ),
      );
    }

    items.forEach((item) => {
      const name = String(item.name || "").trim();
      const label = `${capitalize(type)} · ${name || "(unnamed)"}`;
      const parsed = parseStructuredName(name);

      if (!name) {
        findings.push(finding("naming", "Missing name", "error", label, "Provide a descriptive name."));
        return;
      }
      if (PLACEHOLDER_NAME.test(name)) {
        findings.push(
          finding(
            "naming",
            "Placeholder name",
            "warning",
            label,
            "Replace generic test, copy, or untitled wording with its business purpose.",
          ),
        );
      }
      if (name.length > 80) {
        findings.push(
          finding("naming", "Overlong name", "info", label, "Name exceeds 80 characters and may be difficult to scan."),
        );
      }
      if (!parsed) {
        findings.push(
          finding(
            "naming",
            "Unstructured name",
            "error",
            label,
            "The name does not expose a consistent prefix and separator.",
          ),
        );
        return;
      }
      if (rule.prefixes?.length && !rule.prefixes.includes(parsed.prefix)) {
        findings.push(
          finding(
            "naming",
            "Unexpected prefix",
            "warning",
            label,
            `Observed “${parsed.prefix}”; inferred vocabulary is ${rule.prefixes.join(", ")}.`,
          ),
        );
      }
      if (parsed.separator !== rule.separator) {
        findings.push(
          finding(
            "naming",
            "Separator mismatch",
            "warning",
            label,
            `Expected ${displaySeparator(rule.separator)} after the prefix.`,
          ),
        );
      }
      const actualCasing = detectCasing(parsed.remainder);
      if (rule.casing !== "mixed" && actualCasing !== rule.casing) {
        findings.push(
          finding(
            "naming",
            "Casing mismatch",
            "warning",
            label,
            `Expected ${casingLabel(rule.casing)}; detected ${casingLabel(actualCasing)}.`,
          ),
        );
      }
    });

    for (let index = 0; index < items.length; index += 1) {
      const left = String(items[index].name || "");
      const leftNormalized = normalizeName(left);
      for (let compareIndex = index + 1; compareIndex < items.length; compareIndex += 1) {
        const right = String(items[compareIndex].name || "");
        const rightNormalized = normalizeName(right);
        if (Math.abs(leftNormalized.length - rightNormalized.length) > 2) continue;
        const distance = levenshtein(leftNormalized, rightNormalized, 2);
        if (distance > 2) continue;
        findings.push(
          finding(
            "naming",
            distance === 0 ? "Normalized duplicate" : "Near-duplicate name",
            distance === 0 ? "warning" : "info",
            `${left || "(unnamed)"} ↔ ${right || "(unnamed)"}`,
            distance === 0
              ? "These names become identical after normalization."
              : `Edit distance is ${distance}; review for a typo or duplicate.`,
          ),
        );
      }
    }
  });

  return sortFindings(findings);
}

export function extractGa4Events(tags) {
  return tags
    .filter((tag) => tag.type === "gaawe")
    .map((tag) => {
      const eventName = parameterToValue(findParameter(tag.parameter, "eventName"));
      const params = {};
      ["eventParameters", "eventSettingsTable", "ecommerce"].forEach((key) =>
        Object.assign(params, parameterTableToObject(findParameter(tag.parameter, key))),
      );
      return {
        eventName:
          typeof eventName === "string" && eventName
            ? eventName
            : "(dynamic or missing event name)",
        params,
        item: tag.name || `Tag ${tag.tagId || "unknown"}`,
        paused: tag.paused === true,
        source: "GTM tag",
      };
    });
}

export function validateEvents(events) {
  const findings = [];
  const eventNameGroups = groupBy(
    events.filter((event) => event.source === "GTM tag"),
    (event) => event.eventName,
  );
  eventNameGroups.forEach((group, eventName) => {
    if (group.length < 2 || !eventName || eventName.startsWith("(")) return;
    findings.push(
      finding(
        "events",
        "Duplicate event implementation",
        "info",
        eventName,
        `${group.length} GTM tags emit this event; verify that their trigger scopes do not overlap.`,
      ),
    );
  });

  events.forEach((event) => {
    const { eventName, item } = event;
    if (!eventName || eventName.startsWith("(") || isDynamicValue(eventName)) {
      findings.push(
        finding(
          "events",
          "Dynamic event name",
          "info",
          item,
          "The event name resolves at runtime and cannot be schema-selected statically.",
        ),
      );
      return;
    }

    if (!/^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(eventName)) {
      findings.push(
        finding(
          "events",
          "Invalid event-name format",
          "warning",
          `${item} · ${eventName}`,
          "Use a letter first, then letters, numbers, or underscores, with a maximum of 40 characters.",
        ),
      );
    }
    if (eventName !== eventName.toLowerCase()) {
      findings.push(
        finding(
          "events",
          "Event-name casing",
          "warning",
          `${item} · ${eventName}`,
          "Lowercase snake_case is recommended for consistent GA4 reporting.",
        ),
      );
    }

    const schema = GA4_SCHEMAS[eventName];
    if (!schema) {
      findings.push(
        finding(
          "events",
          "Custom event",
          "info",
          `${item} · ${eventName}`,
          "This event is outside the curated recommended-event table and was not schema-validated.",
        ),
      );
      return;
    }

    const params = event.params || {};
    schema.required.forEach((key) => {
      if (!hasMeaningfulValue(params[key])) {
        findings.push(
          finding(
            "events",
            "Missing required parameter",
            "error",
            `${item} · ${eventName}`,
            `Required parameter “${key}” is missing or empty.`,
          ),
        );
      }
    });

    Object.entries(schema.types).forEach(([key, expected]) => {
      if (!Object.prototype.hasOwnProperty.call(params, key) || !hasMeaningfulValue(params[key])) {
        return;
      }
      if (isDynamicValue(params[key])) {
        findings.push(
          finding(
            "events",
            "Runtime type unresolved",
            "info",
            `${item} · ${key}`,
            `A GTM variable supplies this value; expected type is ${expected}.`,
          ),
        );
      } else if (!matchesType(params[key], expected)) {
        findings.push(
          finding(
            "events",
            "Parameter type mismatch",
            "warning",
            `${item} · ${key}`,
            `Expected ${expected}; received ${describeType(params[key])}.`,
          ),
        );
      }
    });

    if (Object.prototype.hasOwnProperty.call(params, "items")) {
      validateItems(params.items, schema, item, eventName, findings);
    }

    const allowed = new Set([...schema.required, ...Object.keys(schema.types)]);
    const extra = Object.keys(params).filter(
      (key) => !allowed.has(key) && key !== "gtm.uniqueEventId",
    );
    if (extra.length) {
      findings.push(
        finding(
          "events",
          "Non-schema parameters",
          "info",
          `${item} · ${eventName}`,
          `Additional parameters: ${extra.join(", ")}.`,
        ),
      );
    }
    if (event.paused) {
      findings.push(
        finding("events", "Paused GA4 tag", "info", item, "This source tag is currently paused."),
      );
    }
  });

  return sortFindings(findings);
}

function validateItems(items, schema, item, eventName, findings) {
  if (isDynamicValue(items)) {
    findings.push(
      finding(
        "events",
        "Runtime items unresolved",
        "info",
        `${item} · items`,
        "A GTM variable supplies items; verify nested values with a live dataLayer payload.",
      ),
    );
    return;
  }
  if (!Array.isArray(items)) return;
  if (!items.length) {
    findings.push(
      finding(
        "events",
        "Empty items array",
        "error",
        `${item} · ${eventName}`,
        "The ecommerce event contains an empty items array.",
      ),
    );
    return;
  }

  items.forEach((product, index) => {
    const label = `${item} · item ${index + 1}`;
    if (!product || typeof product !== "object" || Array.isArray(product)) {
      findings.push(
        finding("events", "Invalid item object", "error", label, "Each items[] entry must be an object."),
      );
      return;
    }
    if (schema.itemAny && !schema.itemAny.some((key) => hasMeaningfulValue(product[key]))) {
      findings.push(
        finding(
          "events",
          "Missing item identity",
          "error",
          label,
          `Provide at least one of: ${schema.itemAny.join(" or ")}.`,
        ),
      );
    }
    asArray(schema.itemRequired).forEach((key) => {
      if (!hasMeaningfulValue(product[key])) {
        findings.push(
          finding("events", "Missing item parameter", "error", label, `Item parameter “${key}” is missing.`),
        );
      }
    });
    Object.entries(schema.itemTypes || {}).forEach(([key, expected]) => {
      if (!Object.prototype.hasOwnProperty.call(product, key) || !hasMeaningfulValue(product[key])) {
        return;
      }
      if (!matchesType(product[key], expected)) {
        findings.push(
          finding(
            "events",
            "Item type mismatch",
            "warning",
            `${label} · ${key}`,
            `Expected ${expected}; received ${describeType(product[key])}.`,
          ),
        );
      }
    });
  });
}

export function calculateRatings({ container, findings, eventCount }) {
  const objectCount = container.tags.length + container.triggers.length + container.variables.length;
  const health = rateSection(findings.filter((item) => item.tabId === "health"), objectCount);
  const naming = rateSection(findings.filter((item) => item.tabId === "naming"), objectCount);
  const measurement =
    eventCount > 0
      ? rateSection(findings.filter((item) => item.tabId === "events"), eventCount * 2)
      : { score: null, grade: "N/A", label: "Not assessed", counts: emptySeverityCounts() };
  const assessed = [
    { result: health, weight: 0.45 },
    { result: naming, weight: 0.35 },
    ...(measurement.score === null ? [] : [{ result: measurement, weight: 0.2 }]),
  ];
  const weightTotal = assessed.reduce((sum, item) => sum + item.weight, 0);
  const overallScore = Math.round(
    assessed.reduce((sum, item) => sum + item.result.score * item.weight, 0) / weightTotal,
  );

  return {
    overall: {
      score: overallScore,
      grade: gradeForScore(overallScore),
      label: labelForScore(overallScore),
      counts: countSeverities(findings),
    },
    health,
    naming,
    measurement,
  };
}

function rateSection(findings, scope) {
  const counts = countSeverities(findings);
  const risk = counts.error * 10 + counts.warning * 4 + counts.info;
  const capacity = Math.max(1, scope) * 12;
  const score = Math.max(0, Math.round(100 - Math.min(100, (risk / capacity) * 100)));
  return {
    score,
    grade: gradeForScore(score),
    label: labelForScore(score),
    counts,
  };
}

export function makeInventory(container) {
  return [
    ...container.tags.map((item) => ({
      objectType: "Tag",
      name: item.name || "(unnamed)",
      subtype: item.type || "unknown",
      id: String(item.tagId || ""),
      status: item.paused ? "Paused" : "Active",
    })),
    ...container.triggers.map((item) => ({
      objectType: "Trigger",
      name: item.name || "(unnamed)",
      subtype: item.type || "unknown",
      id: String(item.triggerId || ""),
      status: "Active",
    })),
    ...container.variables.map((item) => ({
      objectType: "Variable",
      name: item.name || "(unnamed)",
      subtype: item.type || "unknown",
      id: String(item.variableId || ""),
      status: "Active",
    })),
  ];
}

export function buildAiAuditPayload(container, analysis) {
  const names = {
    tags: container.tags.slice(0, 120).map((item) => ({
      name: sanitizeForAI(item.name),
      type: sanitizeForAI(item.type),
      paused: item.paused === true,
    })),
    triggers: container.triggers.slice(0, 120).map((item) => ({
      name: sanitizeForAI(item.name),
      type: sanitizeForAI(item.type),
    })),
    variables: container.variables.slice(0, 120).map((item) => ({
      name: sanitizeForAI(item.name),
      type: sanitizeForAI(item.type),
    })),
  };

  return {
    scope: {
      counts: analysis.counts,
      containerLabel: sanitizeForAI(container.metadata.name),
    },
    deterministicRatings: analysis.ratings,
    inferredNamingRules: analysis.namingRules,
    findings: analysis.findings.slice(0, 80).map((item) => ({
      tabId: item.tabId,
      category: item.category,
      severity: item.severity,
      item: sanitizeForAI(item.item),
      description: sanitizeForAI(item.description),
    })),
    names,
    privacyNote:
      "Raw parameter values, custom HTML, account IDs, trigger IDs, variable values, and the original JSON are excluded.",
  };
}

export function countSeverities(findings) {
  return findings.reduce(
    (counts, item) => {
      counts[item.severity] += 1;
      return counts;
    },
    emptySeverityCounts(),
  );
}

export function casingLabel(value) {
  return {
    title: "Title Case",
    snake: "snake_case",
    kebab: "kebab-case",
    lower: "lower case",
    upper: "UPPER CASE",
    mixed: "mixed casing",
  }[value] || value;
}

export function displaySeparator(separator) {
  if (separator === " ") return "a space";
  return `“${String(separator).replaceAll(" ", "·")}”`;
}

function findParameter(parameters, key) {
  return asArray(parameters).find((parameter) => parameter?.key === key);
}

function parameterToValue(node) {
  if (!node || typeof node !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(node, "value")) return node.value;
  if (Array.isArray(node.list)) return node.list.map(parameterToValue);
  if (Array.isArray(node.map)) {
    return node.map.reduce((accumulator, entry) => {
      if (entry?.key) accumulator[entry.key] = parameterToValue(entry);
      return accumulator;
    }, {});
  }
  return undefined;
}

function parameterTableToObject(node) {
  if (!node) return {};
  return asArray(node.list).reduce((accumulator, row) => {
    const entries = asArray(row?.map);
    const nameEntry = entries.find((entry) => ["name", "parameter", "key"].includes(entry.key));
    const valueEntry = entries.find((entry) => entry.key === "value");
    const name = parameterToValue(nameEntry);
    if (typeof name === "string" && name) accumulator[name] = parameterToValue(valueEntry);
    return accumulator;
  }, {});
}

function collectVariableReferences(value, target) {
  if (typeof value === "string") {
    const regex = /{{\s*([^{}]+?)\s*}}/g;
    let match;
    while ((match = regex.exec(value))) target.add(match[1].trim());
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectVariableReferences(item, target));
    return;
  }
  if (value && typeof value === "object") {
    Object.values(value).forEach((item) => collectVariableReferences(item, target));
  }
}

function isReservedTrigger(trigger) {
  const id = String(trigger.triggerId);
  if (RESERVED_TRIGGER_IDS.has(id)) return true;
  return ["all pages", "initialization", "consent initialization"].includes(
    String(trigger.name || "").toLowerCase(),
  );
}

function hasExplicitConsent(tag) {
  const settings = tag.consentSettings;
  if (!settings || typeof settings !== "object") return false;
  if (settings.consentStatus === "notNeeded") return true;
  return (
    settings.consentStatus === "needed" &&
    asArray(settings.consentType?.list).length > 0
  );
}

function stripIdentityFields(value) {
  if (Array.isArray(value)) return value.map(stripIdentityFields);
  if (!value || typeof value !== "object") return value;
  return Object.keys(value)
    .filter(
      (key) =>
        ![
          "triggerId",
          "tagId",
          "variableId",
          "name",
          "accountId",
          "containerId",
          "fingerprint",
        ].includes(key),
    )
    .sort()
    .reduce((accumulator, key) => {
      accumulator[key] = stripIdentityFields(value[key]);
      return accumulator;
    }, {});
}

function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function matchesType(value, expected) {
  if (expected === "array") return Array.isArray(value);
  if (expected === "currency") return typeof value === "string" && /^[A-Z]{3}$/.test(value);
  return typeof value === expected && (expected !== "number" || Number.isFinite(value));
}

function hasMeaningfulValue(value) {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    (!Array.isArray(value) || value.length > 0)
  );
}

function isDynamicValue(value) {
  return typeof value === "string" && /{{[^{}]+}}/.test(value);
}

function describeType(value) {
  if (Array.isArray(value)) return "array";
  if (value === null) return "null";
  if (typeof value === "string" && /^[A-Za-z]{3}$/.test(value)) return `string “${value}”`;
  return typeof value;
}

function finding(tabId, category, severity, item, description) {
  return { tabId, category, severity, item, description };
}

function addFindingIds(findings) {
  return findings.map((item, index) => ({
    ...item,
    id: `${item.tabId}-${item.severity}-${index + 1}`,
  }));
}

function sortFindings(findings) {
  return [...findings].sort((left, right) => {
    const severity = SEVERITY_ORDER[left.severity] - SEVERITY_ORDER[right.severity];
    if (severity !== 0) return severity;
    const tab = left.tabId.localeCompare(right.tabId);
    if (tab !== 0) return tab;
    return left.category.localeCompare(right.category);
  });
}

function normalizeName(value) {
  return String(value).trim().toLowerCase().replace(/\s+/g, " ");
}

function levenshtein(left, right, stopAt = Infinity) {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;
  if (Math.abs(left.length - right.length) > stopAt) return stopAt + 1;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex];
    let rowMinimum = current[0];
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1;
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + cost,
      );
      rowMinimum = Math.min(rowMinimum, current[rightIndex]);
    }
    if (rowMinimum > stopAt) return stopAt + 1;
    previous = current;
  }
  return previous[right.length];
}

function sanitizeForAI(value) {
  return String(value ?? "")
    .slice(0, 180)
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[redacted-email]")
    .replace(/\b(?:sk|or)-[A-Za-z0-9_-]{12,}\b/g, "[redacted-key]")
    .replace(/https?:\/\/\S+/gi, "[redacted-url]")
    .replace(/\b(?:trigger|tag|variable|account|container)\s+ID\s+[A-Za-z0-9_-]+\b/gi, (match) => {
      const label = match.slice(0, match.toLowerCase().indexOf("id") + 2);
      return `${label} [redacted]`;
    })
    .replace(/\bGTM-[A-Z0-9]+\b/gi, "[redacted-container-id]")
    .replace(/\b\d{10,}\b/g, "[redacted-number]");
}

function countBy(values) {
  return values.reduce((map, value) => {
    map.set(value, (map.get(value) || 0) + 1);
    return map;
  }, new Map());
}

function topEntry(map) {
  return [...map.entries()].sort((left, right) => right[1] - left[1])[0];
}

function groupBy(items, getKey) {
  return items.reduce((map, item) => {
    const key = getKey(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
    return map;
  }, new Map());
}

function gradeForScore(score) {
  if (score >= 92) return "A";
  if (score >= 84) return "B";
  if (score >= 72) return "C";
  if (score >= 60) return "D";
  return "F";
}

function labelForScore(score) {
  if (score >= 92) return "Excellent";
  if (score >= 84) return "Strong";
  if (score >= 72) return "Good with gaps";
  if (score >= 60) return "Needs attention";
  return "High risk";
}

function emptySeverityCounts() {
  return { error: 0, warning: 0, info: 0 };
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function capitalize(value) {
  return value ? value[0].toUpperCase() + value.slice(1) : "";
}

function friendlyJsonError(error) {
  return (error instanceof Error ? error.message : String(error)).replace(/^JSON\.parse:\s*/i, "");
}
